import { EventBase } from '../event/event-base';

export class LinkBase extends EventBase {

    /* ================================================================
    Link-Specific Component State Flags
    ===================================================================*/

    protected isDescriptionRequired = false;
    protected isCtaEnabled = false;
    protected isExpiryDateRequired = true;

    // Override event-specific flags for links
    protected addEventStatus = false; // Links use different status
    protected addLinkStatus = true;

    /* ================================================================
    Link-Specific Properties
    ===================================================================*/

    protected linkType: string = 'Links / Call to Action';
    protected description: string = '';
    protected ctaText: string = '';
    protected ctaLink: string = '';
    protected expiryDate: string = '';
    protected expiryDateObj: Date | null = null;

    // Link subtypes
    protected linkSubTypes = [
        'hyperlink',
        'googleForm',
        'feePayment',
        'externalApi',
        'others'
    ];

    constructor() {
        super();

        // Set link-specific page name
        this.pageName = 'Create Link';

        // Set expiry date to 30 days from now by default
        const defaultExpiry = new Date();
        defaultExpiry.setDate(defaultExpiry.getDate() + 30);
        this.expiryDateObj = defaultExpiry;
        this.expiryDate = this.formatDateForInput(defaultExpiry);
    }

    /* ================================================================
    Link-Specific Toggle Methods
    ===================================================================*/

    /**
     * Toggle CTA functionality
     * @param that Component instance
     * @param enabled Boolean state to set
     */
    protected toggleCTA(that: any, enabled: boolean): void {
        that.isCtaEnabled = enabled;
        if (that.facility) {
            that.facility.ctaEnabled = enabled;
            if (!enabled) {
                that.facility.ctaText = '';
                that.facility.ctaLink = '';
            }
        }
    }

    /**
     * Toggle description requirement
     * @param that Component instance
     * @param enabled Boolean state to set
     */
    protected toggleDescriptionRequired(that: any, enabled: boolean): void {
        that.isDescriptionRequired = enabled;
        if (that.facility) {
            that.facility.isDescriptionRequired = enabled;
            if (!enabled) {
                that.facility.description = '';
            }
        }
    }

    /* ================================================================
    Link-Specific Date Methods
    ===================================================================*/

    /**
     * Handle expiry date change for links
     */
    protected onExpiryDateChange(that: any, date: Date): void {
        if (date) {
            const year = date.getFullYear();
            const month = ('0' + (date.getMonth() + 1)).slice(-2);
            const day = ('0' + date.getDate()).slice(-2);
            that.facility.expiryDate = `${year}-${month}-${day}`;
            that.expiryDateObj = date;

            // Validate if validation object exists
            if (that.linkValidationObj) {
                that.linkValidationObj.validateFacility(that, 'facility.expiryDate', '');
            }
        }
    }

    /**
     * Format date for input field (YYYY-MM-DD format) - PROTECTED to allow override
     */
    protected formatDateForInput(date: Date): string {
        if (!date || isNaN(date.getTime())) {
            return '';
        }

        try {
            const year = date.getFullYear();
            const month = ('0' + (date.getMonth() + 1)).slice(-2);
            const day = ('0' + date.getDate()).slice(-2);
            return `${year}-${month}-${day}`;
        } catch (error) {
            console.error('Error formatting date for input:', error);
            return '';
        }
    }

    /* ================================================================
    Status Management Methods (Link-specific)
    ===================================================================*/

    /**
     * Change status for view Link mode
     */
    changeStatus(that: any): void {
        try {
            that.isReadOnly = !that.isReadOnly;
            that.editStatus = !that.editStatus;
            that.pageName = that.editStatus ? 'Edit Link' : 'View Link';
            this.setLocalFlagWithLinkRules(that);
            this.setDefaultValues(that);
        } catch (err) {
            console.log(err);
        }
    }

    /**
     * Set local flags with link rules
     */
    protected setLocalFlagWithLinkRules(that: any): void {
        // Call parent method first
        this.setLocalFlagWithEventRules(that);

        // Set link-specific flags
        that.isCtaEnabled = that.facility.ctaEnabled || false;
        that.isDescriptionRequired = that.facility.isDescriptionRequired || false;
        that.linkType = that.facility.linkType || 'Links / Call to Action';

        // Ensure link-specific category
        that.facility.category = 'Links';
        that.facility.eventType = 'Links / Call to Action';

        // Set link-specific timing defaults (00:00 as per requirement)
        that.facility.timings.openTime = '00:00';
        that.facility.timings.closeTime = '00:00';
        that.openTime_hour = '00';
        that.openTime_minute = '00';
        that.closeTime_hour = '00';
        that.closeTime_minute = '00';
    }

    /* ================================================================
    Default Value Management (Link-specific)
    ===================================================================*/

    /**
     * Set Default Values for links
     */
    setDefaultValues(that: any): void {
        try {
            // Call parent method first
            super.setDefaultValues(that);

            // Link-specific default values
            if (that.facility.description == null || that.facility.description === undefined) {
                that.facility.description = '';
            }

            if (that.facility.ctaText == null || that.facility.ctaText === undefined) {
                that.facility.ctaText = '';
            }

            if (that.facility.ctaLink == null || that.facility.ctaLink === undefined) {
                that.facility.ctaLink = '';
            }

            if (that.facility.ctaEnabled == null || that.facility.ctaEnabled === undefined) {
                that.facility.ctaEnabled = false;
            }

            if (that.facility.linkType == null || that.facility.linkType === undefined) {
                that.facility.linkType = 'Links / Call to Action';
            }

            if (that.facility.expiryDate == null || that.facility.expiryDate === undefined) {
                const defaultExpiry = new Date();
                defaultExpiry.setDate(defaultExpiry.getDate() + 30);
                that.facility.expiryDate = this.formatDateForInput(defaultExpiry);
                that.facility.expiryDateObj = defaultExpiry;
            }

            // Override parent event-specific defaults for links
            that.facility.category = 'Links';
            that.facility.eventType = 'Links / Call to Action';

            // Set timing defaults for links (00:00 as per requirement)
            that.facility.timings.openTime = '00:00';
            that.facility.timings.closeTime = '00:00';

        } catch (err) {
            console.log(err);
        }
    }

    /* ================================================================
    View Preparation Methods (Link-specific)
    ===================================================================*/

    /**
     * Set flags before viewing link
     */
    protected setFlagBeforeViewLink(that: any): void {
        that.viewIcon.name = '';
        that.viewIcon.iconClass = '';

        // Clear timing arrays (not used for links)
        that.facility.peekTimings = [];
        that.peekTimingsArray = [];
        that.closeTimeArray = [];

        that.defaultValue = true;
        that.saveFlag = true;
        that.functionHallFlag = false;
        that.viewStatus = true;
        that.isReadOnly = true;
        that.editStatus = false;
        that.addLinkStatus = false;

        // Ensure link-specific category and disabled features
        that.facility.category = 'Links';
        that.facility.isPeakTimeEnabled = false;
        that.facility.isBreakTimeEnabled = false;
        that.facility.isSlotsLocked = true;

        // Set timing defaults
        that.facility.timings.openTime = '00:00';
        that.facility.timings.closeTime = '00:00';
    }

    /**
     * Set timings for link viewing (simplified for links)
     */
    protected setTimingsForLink(that: any): void {
        // For links, we always use 00:00 times as per requirement
        that.openTime_hour = '00';
        that.openTime_minute = '00';
        that.closeTime_hour = '00';
        that.closeTime_minute = '00';

        // Ensure facility timings match
        if (!that.facility.timings) {
            that.facility.timings = {};
        }
        that.facility.timings.openTime = '00:00';
        that.facility.timings.closeTime = '00:00';
    }

    /* ================================================================
    Link-Specific Helper Methods
    ===================================================================*/

    /**
     * Enforce link-specific business rules
     */
    protected enforceLinkRules(that: any): void {
        // Always disable peak and break times for links
        that.facility.isPeakTimeEnabled = false;
        that.facility.isBreakTimeEnabled = false;
        that.isPeakTimeEnabled = false;
        that.isBreakTimeEnabled = false;

        // Always lock slots for links
        that.facility.isSlotsLocked = true;
        that.isSlotsLocked = true;

        // Clear peak and break timing arrays
        that.facility.peekTimings = [];
        that.facility.breakTimings = [];
        that.peekTimingsArray = [];
        that.closeTimeArray = [];

        // Set fixed category
        that.facility.category = 'Links';
        that.facility.eventType = 'Links / Call to Action';

        // Clear peak units (not applicable to links)
        that.facility.peakUnitsCount = '';

        // Set default timing values for links
        that.facility.timings.openTime = '00:00';
        that.facility.timings.closeTime = '00:00';
        that.facility.slotDuration = '0';
    }

    /**
     * Apply link-specific fields to facility object before saving
     */
    protected applyLinkFieldsToFacility(that: any): void {
        try {
            // Apply link-specific fields
            that.facility.linkType = that.linkType || 'Links / Call to Action';
            that.facility.description = that.description || '';
            that.facility.ctaEnabled = that.isCtaEnabled || false;
            that.facility.ctaText = that.ctaText || '';
            that.facility.ctaLink = that.ctaLink || '';

            // Ensure expiry date is set
            if (that.expiryDateObj) {
                that.facility.expiryDate = this.formatDateForInput(that.expiryDateObj);
            }

            // Enforce link rules
            this.enforceLinkRules(that);

        } catch (err) {
            console.error('Error applying link fields to facility:', err);
        }
    }

    /**
     * Initialize default link structure
     */
    protected initializeLinkDefaults(that: any): void {
        // Initialize 5-tier pricing structure (same as events)
        if (!that.facility.activityCharges) {
            that.facility.activityCharges = {
                member: { enabled: false, amount: '' },
                nonMember: { enabled: false, amount: '' },
                guest: { enabled: false, amount: '' },
                corpMember: { enabled: false, amount: '' },
                corpNonMember: { enabled: false, amount: '' }
            };
        }

        // Set default waitlist count
        if (that.isWaitlistEnabled && (!that.facility.waitlistCount || that.facility.waitlistCount === '')) {
            that.facility.waitlistCount = '3';
        }

        // Initialize link-specific fields
        that.facility.linkType = that.facility.linkType || 'Links / Call to Action';
        that.facility.description = that.facility.description || '';
        that.facility.ctaEnabled = that.facility.ctaEnabled || false;
        that.facility.ctaText = that.facility.ctaText || '';
        that.facility.ctaLink = that.facility.ctaLink || '';

        // Set default expiry date (30 days from now)
        if (!that.facility.expiryDate) {
            const defaultExpiry = new Date();
            defaultExpiry.setDate(defaultExpiry.getDate() + 30);
            that.facility.expiryDate = this.formatDateForInput(defaultExpiry);
            that.facility.expiryDateObj = defaultExpiry;
        }

        // Enforce link rules
        this.enforceLinkRules(that);
    }

    /* ================================================================
    Image Upload Methods (Missing methods that caused errors)
    ===================================================================*/

    /**
     * Upload image for link
     */
   /**
 * Upload image for link
 */
protected uploadImage(that: any, event: any, alertImagePopUpObj?: any): void {
    try {
        const file = event.target.files[0];
        if (file) {
            // Check file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
            if (allowedTypes.indexOf(file.type) < 0) {
                if (alertImagePopUpObj && alertImagePopUpObj.showChildModal) {
                    that.popUpMessage = 'Please select a valid image file (JPEG, PNG, GIF, or WebP)';
                    that.popupTitle = 'Invalid File Type';
                    alertImagePopUpObj.showChildModal();
                }
                return;
            }

            // Check file size (limit to 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                if (alertImagePopUpObj && alertImagePopUpObj.showChildModal) {
                    that.popUpMessage = 'Image file size must be less than 5MB';
                    that.popupTitle = 'File Too Large';
                    alertImagePopUpObj.showChildModal();
                }
                return;
            }

            // Read and set image
            const reader = new FileReader();
            reader.onload = (e: any) => {
                that.facility.imageUrl = e.target.result;
                that.facility.imageFile = file;
            };
            reader.readAsDataURL(file);
        }
    } catch (error) {
        console.error('Error uploading image:', error);
    }
}


    /**
     * Clear uploaded image
     */
    protected clearImage(that: any): void {
        try {
            if (that.facility) {
                that.facility.imageUrl = '';
                that.facility.imageFile = null;
            }

            // Clear file input if available
            const fileInput = document.getElementById('linkImageUpload') as HTMLInputElement;
            if (fileInput) {
                fileInput.value = '';
            }
        } catch (error) {
            console.error('Error clearing image:', error);
        }
    }

    /* ================================================================
    Validation Helper Methods
    ===================================================================*/

    /**
     * Validate link expiry date
     */
    protected validateExpiryDate(that: any): boolean {
        if (!that.facility.expiryDate) {
            return false;
        }

        const expiryDate = new Date(that.facility.expiryDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        expiryDate.setHours(0, 0, 0, 0);

        // Expiry date should be in the future
        return expiryDate > today;
    }

    /**
     * Validate CTA settings
     */
    protected validateCTA(that: any): boolean {
        if (!that.facility.ctaEnabled) {
            return true; // CTA is optional
        }

        // If CTA is enabled, both text and link are required
        return !!(that.facility.ctaText && that.facility.ctaText.trim() &&
                 that.facility.ctaLink && that.facility.ctaLink.trim());
    }

    /**
     * Validate URL format
     */
    protected validateUrl(url: string): boolean {
        if (!url || url.trim() === '') {
            return true; // Empty URL is valid (optional field)
        }

        try {
            new URL(url);
            return true;
        } catch (e) {
            // Check if it's a relative URL or starts with common protocols
            const urlPattern = /^(https?:\/\/|ftp:\/\/|mailto:|tel:|\/)[\w\-._~:/?#[\]@!$&'()*+,;=]+$/i;
            return urlPattern.test(url.trim());
        }
    }

    /* ================================================================
    Legacy Methods (kept for backward compatibility)
    ===================================================================*/

    setFlagBeforeViewFacility(that: any): void {
        this.setFlagBeforeViewLink(that);
    }

    setTimingsForFacility(that: any): void {
        this.setTimingsForLink(that);
    }

    setLocalFlagWithFacilityRules(that: any): void {
        this.setLocalFlagWithLinkRules(that);
    }
}



// WEBPACK FOOTER //
// ./src/app/facility/link/link-base.ts